import React from "react";
import { useModal } from "../../context/modal_provider";
import {
  Box,
  Button,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Divider,
  CircularProgress,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import useBackend from "../../hooks/use_backend";
import { createSiteAlertMute } from "../../scripts/api_v2/alerting_client";
import Snackbar from "../../scripts/snackbar";

export function useMuteAlertDialog() {
  const { openDialogModal } = useModal();
  const params = useParams();

  return (alert, mutedAlerts = [], onMute, onUnmute) => {
    openDialogModal({
      dialogTabs: [
        {
          label: "Mute Alert",
          component: (
            <MuteAlertDialog
              alert={alert}
              mutedAlerts={mutedAlerts}
              onMute={onMute}
              onUnmute={onUnmute}
              siteId={params.site_id}
            />
          ),
        },
      ],
    });
  };
}

export function MuteAlertDialog({ alert, mutedAlerts, onMute, onUnmute, siteId }) {
  const { closeDialogModal } = useModal();
  const [duration, setDuration] = React.useState("1week");
  const [customDate, setCustomDate] = React.useState(null);
  const backend = useBackend();
  const queryClient = useQueryClient();

  // Calculate max date (1 month from now)
  const maxDateTime = React.useMemo(() => {
    const oneMonthFromNow = new Date();
    oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
    return oneMonthFromNow;
  }, []);

  // Handle custom date change with validation
  const handleCustomDateChange = (newValue) => {
    if (newValue && newValue > maxDateTime) {
      // If the typed date is beyond 1 month, reset to null or show error
      setCustomDate(null);
      Snackbar.error("Selected date cannot be more than 1 month in advance");
    } else {
      setCustomDate(newValue);
    }
  };

  // Mutation to create mute
  const muteMutation = useMutation({
    mutationFn: (muteData) => {
      return createSiteAlertMute(backend.fetch, siteId, muteData);
    },
    onSuccess: async () => {
      // Invalidate both alerts and mutes to trigger refetch
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.SITE_ALERTS, backend.fetch, siteId]);
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.ALERT_MUTES, backend.fetch, siteId]);
      Snackbar.success("Alert type muted successfully");
      if (onMute) {
        onMute();
      }
      closeDialogModal();
    },
    onError: (error) => {
      Snackbar.toast(
        <>
          Failed to mute alert: <br /> {error.message}
        </>,
        "error",
        5000,
      );
    },
  });

  const handleMute = () => {
    const muteData = {
      alert_code: alert?.alert_code,
      info: alert?.info,
      device_id: alert?.device_id || null,
      duration_type: duration === "custom" ? null : duration,
      valid_to_date: duration === "custom" && customDate ? customDate.toISOString() : null,
    };

    muteMutation.mutate(muteData);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minWidth: 500, p: 2 }}>
        <Stack spacing={3}>
          {/* Alert Info */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Alert Information
            </Typography>
            {alert?.alert_code && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                <strong>Code:</strong> {alert.alert_code}
              </Typography>
            )}
            {alert?.device_id && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                <strong>Device ID:</strong> {alert.device_id}
              </Typography>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="body2" color="text.secondary">
              This action will mute all alerts of type <i>(for this site only)</i>:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: "bold" }}>
              {alert?.info || "N/A"}
            </Typography>
            {alert?.device_id && (
              <>
                <Typography variant="body2" color="text.secondary">
                  For device:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: "bold" }}>
                  {alert?.device_id}
                </Typography>
              </>
            )}
          </Box>

          {/* Duration Selection */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Duration:
            </Typography>
            <ToggleButtonGroup
              value={duration}
              exclusive
              onChange={(e, newValue) => newValue && setDuration(newValue)}
              fullWidth
              color="primary"
            >
              <ToggleButton value="1day">1 Day</ToggleButton>
              <ToggleButton value="1week">1 Week</ToggleButton>
              <ToggleButton value="1month">1 Month</ToggleButton>
              <ToggleButton value="permanent">Permanent</ToggleButton>
            </ToggleButtonGroup>

            <Button variant="outlined" fullWidth sx={{ mt: 2 }} onClick={() => setDuration("custom")}>
              Custom Date
            </Button>

            {duration === "custom" && (
              <DateTimePicker
                label="Mute Until"
                value={customDate}
                onChange={handleCustomDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
                minDateTime={new Date()}
                maxDateTime={maxDateTime}
                sx={{ mt: 2 }}
              />
            )}
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={closeDialogModal} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleMute}
              variant="contained"
              startIcon={
                muteMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <NotificationsOffIcon />
              }
              disabled={!alert || (duration === "custom" && !customDate) || muteMutation.isPending}
            >
              {muteMutation.isPending ? "Muting..." : "Mute Alert"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
}
